import pick from 'lodash/pick';
import store from '@Store';
import { citations } from '@Constants';
import { assert } from '@Utils/assert';
import Breadcrumb from '@Services/Breadcrumb';
import Catalyst from '@Services/Catalyst';

import { searchFilterAsync } from '@Containers/ResultsFilters/ResultsFiltersService';
import { activeBreadcrumbForDb } from '@Containers/Breadcrumbs/BreadcrumbsSelectors';
import { open as openZeroResultDialog } from '@Containers/ResultsFilters/ResultsFiltersComponents/ZeroFilterResults/ZeroFilterResultsActions';
import { generateHeatmapPath, generateResultsPath } from '@AppRoutes/utils';
import history from '@AppRoutes/history';
import { getCombinedRouteParams } from '@AppRoutes/AppRoutesSelectors';
import { preparePropertiesForStoreInBreadCrumb } from './preparePropertiesUtils';
import prepareBreadCrumb from './prepareBreadCrumb';

import {
  nextStateBaseParams as nextStateBaseParamsSelector,
  searchFilterBaseParams as searchFilterBaseParamsSelector,
  searchFilterCitationsAdditionalParams as searchFilterCitationsAdditionalParamsSelector,
} from './performSearchFilterAsyncSelectors';

import { extractFacetsAndProperties } from './facetsForTrackingUtils';

function track(properties, baseParamsExtension, context) {
  const action = baseParamsExtension.exclude ? 'exclude' : 'limit';

  properties.forEach((item) => {
    Catalyst.trackProductFeature([Catalyst.getPrefix(), 'facet', action, context, item.title].join(':'));
  });
}

export function noResultsHandler(paramsForSearchFilters, searchFilterResult) {
  Logger.info('searchFilterAsync did not find any results,', 'params ', paramsForSearchFilters, 'response ', searchFilterResult);

  const { message } = searchFilterResult;

  if (message === 'Started process was canceled;') {
    return;
  }

  store.dispatch(openZeroResultDialog());
}

export function getContextFromBreadcrumb(state, searchFilterBaseParams) {
  const crumbFromStore = activeBreadcrumbForDb(state);

  if (crumbFromStore) {
    return crumbFromStore.get('context');
  }

  return Breadcrumb.getActiveBreadcrumbForDb(searchFilterBaseParams.databaseId).context; // fallback if breadcrumb on angular enabled
}

export default async function performSearchFilterAsync({
  filters,
  properties: propertiesWithFacets,
  browserInteraction,
  baseParamsExtension = {},
}) {
  assert(filters !== undefined, `filters required parameter`);
  assert(propertiesWithFacets !== undefined, `properties required parameter`);

  const state = store.getState();
  const searchFilterBaseParams = searchFilterBaseParamsSelector(state);
  const contextFromCrumb = getContextFromBreadcrumb(state, searchFilterBaseParams);
  const { properties, facets } = extractFacetsAndProperties(propertiesWithFacets);

  const paramsForSearchFilters = {
    ...searchFilterBaseParams,
    ...(contextFromCrumb === citations && searchFilterCitationsAdditionalParamsSelector(state)),
    ...baseParamsExtension,
    properties,
  };

  track(properties, baseParamsExtension, searchFilterBaseParams.context);

  const searchFilterResult = await searchFilterAsync(paramsForSearchFilters);

  if (browserInteraction) {
    browserInteraction.update(searchFilterResult);
  }

  if (searchFilterResult.resultSize === 0) {
    noResultsHandler(paramsForSearchFilters, searchFilterResult);
    return;
  }

  const responseItem = searchFilterResult.results[contextFromCrumb];

  const breadcrumb = prepareBreadCrumb({
    state,
    facets,
    responseItem,
    searchFilterResult,
    baseParamsExtension,
    searchFilterBaseParams,
    heatmapMode: history.location.pathname.indexOf('heatmap') > -1,
  });
  const nextStateBaseParams = nextStateBaseParamsSelector(state);
  const propertiesForBreadCrumb = preparePropertiesForStoreInBreadCrumb(properties, filters, state);

  await Breadcrumb.createBreadcrumb(breadcrumb, responseItem.resultSize, propertiesForBreadCrumb);

  const params = {
    ...nextStateBaseParams,
    hitSetKey: searchFilterResult.hitSetKey,
    resultName: searchFilterResult.resultName,
    pageNumber: 1,
    sortedMatrixName: null,
  };
  const routeParams = getCombinedRouteParams(store.getState());
  // Had to use getCombinedRouteParams instead of getRouteParams, because of issue with ConnectedRoute.
  // But getCombinedRouteParams contains many excessive params, like 'resultsCount', which should be omitted for the next state.
  const baseParamsFromRoute = pick(routeParams, ['context', 'databaseId', 'resultName', 'hitSetKey', 'searchId', 'sort', 'order']);
  let combinedParams = { ...baseParamsFromRoute, ...params};
  combinedParams.fromContext = !combinedParams.fromContext ? routeParams.fromContext : combinedParams.fromContext;
  const pathName = breadcrumb.isHeatmapShown
    ? generateHeatmapPath({ ...combinedParams, isHeatmap: true })
    : generateResultsPath(combinedParams);

  history.push({
    pathname: pathName,
    state: combinedParams,
  });
}
